import { Reference } from "@apollo/client";
import { IonList } from "@ionic/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import DirectionButton from "@components/buttons/DirectionButton";
import styles from "@components/ConsultReport.module.css";
import ConsultReportDiscussionItem from "@components/ConsultReportDiscussionItem";
import ConsultReportInfo from "@components/ConsultReportInfo";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import ConsultReportDiscussionItemForm from "@components/forms/ConsultReportDiscussionItemForm";
import InternalNoteForm from "@components/forms/InternalNoteForm";
import InternalNote from "@components/InternalNote";
import Modal, { ModalElement } from "@components/Modal";
import SectionWithBanner from "@components/SectionWithBanner";
import DateGroupBanner from "@components/text/DateGroupBanner";
import ButtonLink from "@components/typography/ButtonLink";
import useDestroyInternalNote from "@hooks/mutations/useDestroyInternalNote";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { DiscussionItemContextEnum } from "@typing/Enums";
import {
  ConsultFragment as Consult,
  ConsultReportDiscussionItemDestroyDocument,
  ConsultReportDiscussionItemDestroyMutation,
  ConsultReportDiscussionItemDestroyMutationVariables,
  ConsultReportDiscussionItemFragment as ConsultReportDiscussionItemType,
  GetJourneyWithClientRolesQuery,
  InternalNoteFragment as InternalNoteType
} from "@typing/Generated";
import { groupByTimestampDay } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  consult: Consult;
  journey: GetJourneyWithClientRolesQuery["journey"];
};

const ConsultReport = ({ consult, journey }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const discussionItemModalOpener = useOpener();
  const discussionItemModalRef = useRef<ModalElement>(null);

  const [discussionItemBeingEdited, setDiscussionItemBeingEdited] = useState<
    ConsultReportDiscussionItemType | undefined
  >(undefined);

  const internalNoteDeleteOpener = useOpener();
  const [internalNoteBeingDeleted, setInternalNoteBeingDeleted] = useState<InternalNoteType | undefined>(undefined);

  const internalNoteModalOpener = useOpener();
  const [internalNoteBeingEdited, setInternalNoteBeingEdited] = useState<InternalNoteType | undefined>(undefined);

  const destroyInternalNote = useDestroyInternalNote({ consultId: consult.id, journeyId: journey.id });

  const handleInternalNoteDelete = useCallback(() => {
    if (internalNoteBeingDeleted) {
      destroyInternalNote({ id: internalNoteBeingDeleted.id });
      setInternalNoteBeingDeleted(undefined);
      internalNoteDeleteOpener.close();
    }
  }, [destroyInternalNote, internalNoteBeingDeleted, internalNoteDeleteOpener]);

  const [destroyConsultReportDiscussionItem] = useMutationWithValidation<
    ConsultReportDiscussionItemDestroyMutation,
    ConsultReportDiscussionItemDestroyMutationVariables
  >(ConsultReportDiscussionItemDestroyDocument, "consultReport", {
    update: (store, { data }) => {
      if (data?.consultReportDiscussionItemDestroy?.id) {
        store.modify({
          fields: {
            consultReportDiscussionItems(cachedRefs: readonly Reference[], { readField }) {
              return cachedRefs.filter(ref => data.consultReportDiscussionItemDestroy?.id !== readField("id", ref));
            }
          },
          id: store.identify({ ...consult.consultReport })
        });
      }
    }
  });

  const handleAddDiscussionItem = useCallback(() => {
    setDiscussionItemBeingEdited(undefined);
    discussionItemModalOpener.open();
  }, [discussionItemModalOpener]);

  const handleEditDiscussionItem = useCallback(
    (discussionItem: ConsultReportDiscussionItemType) => {
      setDiscussionItemBeingEdited(discussionItem);
      discussionItemModalOpener.open();
    },
    [discussionItemModalOpener]
  );

  const handleDeleteDiscussionItem = useCallback(
    (discussionItem: ConsultReportDiscussionItemType) => {
      destroyConsultReportDiscussionItem({ id: discussionItem.id });
    },
    [destroyConsultReportDiscussionItem]
  );

  const handleDiscussionItemError = useCallback(() => {
    if (discussionItemModalRef.current) {
      discussionItemModalRef.current.scrollToTop();
    }
  }, [discussionItemModalRef]);

  const handleStartInternalNoteEdit = useCallback(
    (note: InternalNoteType) => {
      setInternalNoteBeingEdited(note);
      internalNoteModalOpener.open();
    },
    [internalNoteModalOpener]
  );

  const handleStartInternalNoteDelete = useCallback(
    (note: InternalNoteType) => {
      setInternalNoteBeingDeleted(note);
      internalNoteDeleteOpener.open();
    },
    [internalNoteDeleteOpener]
  );

  const internalNoteDayGroups = useMemo(() => {
    if (consult.consultReport?.internalNotes) {
      return groupByTimestampDay<InternalNoteType>(
        consult.consultReport.internalNotes,
        internalNote => internalNote.createdAt,
        intl
      );
    }
    return [];
  }, [consult, intl]);

  const sanitizedSummary = useSanitizeHtml(consult.consultReport?.summary, journey.id, consult.consultReport);

  // In the API it's technically possible for a consult to have a null consult report, because
  // the client gets null back if the report is unpublished. But they should never get to this
  // component if that's the case, and returning here helps the typing below.
  if (!consult.consultReport) {
    return null;
  }

  return (
    <div className="pageContent">
      <DirectionButton
        direction="back"
        routerLink={journeyRoute({
          journeyId: journey.id,
          linkAfterJourney: "/care-profile",
          myExpert
        })}
      />
      <ConsultReportInfo consult={consult} useCase={journey.careProfile.useCase} />
      <SectionWithBanner titleKey="models.consultReport.summaryHeader">
        {consult.consultReport.summary ? (
          <>{sanitizedSummary}</>
        ) : (
          <FormattedMessage id="models.consultReport.noSummary" />
        )}
      </SectionWithBanner>
      <SectionWithBanner titleKey="models.consultReport.discussionItemsHeader">
        {(consult.consultReport.consultReportDiscussionItems?.length ?? 0) > 0 && (
          <IonList className={styles.discussionItemsList}>
            {consult.consultReport.consultReportDiscussionItems.map(discussionItem => (
              <ConsultReportDiscussionItem
                consult={consult}
                context={DiscussionItemContextEnum.CONSULT_REPORT}
                discussionItem={discussionItem}
                journeyId={journey.id}
                key={discussionItem.id}
                onDelete={handleDeleteDiscussionItem}
                onEdit={handleEditDiscussionItem}
              />
            ))}
          </IonList>
        )}
        <ButtonLink onClick={handleAddDiscussionItem}>
          <FormattedMessage id="models.consultReport.addDiscussionItem" />
        </ButtonLink>
      </SectionWithBanner>

      <SectionWithBanner titleKey="models.consultReport.nextConsultHeader">
        <p>
          {journey.careProfile.nextConsult ? (
            <DateStamp absoluteDate value={journey.careProfile.nextConsult} />
          ) : (
            <FormattedMessage id="models.consultReport.noNextConsult" />
          )}
        </p>
      </SectionWithBanner>

      <SectionWithBanner titleKey="models.consultReport.internalNotesHeader">
        <InternalNoteForm consult={consult} journeyId={journey.id} skipCancel />
        <IonList>
          {internalNoteDayGroups.map(group => (
            <div key={`${group.year}-${group.month}-${group.date}`}>
              <p>
                <DateGroupBanner date={group.date} month={group.month} year={group.year} />
              </p>
              <IonList>
                {group.models.map(internalNote => (
                  <InternalNote
                    consult={consult}
                    journeyId={journey.id}
                    key={internalNote.id}
                    note={internalNote}
                    onDelete={handleStartInternalNoteDelete}
                    onEdit={handleStartInternalNoteEdit}
                  />
                ))}
              </IonList>
            </div>
          ))}
        </IonList>
      </SectionWithBanner>

      <DeleteConfirmationAlert
        messageKey="models.internalNote.reallyDelete"
        onDelete={handleInternalNoteDelete}
        opener={internalNoteDeleteOpener}
      />

      <Modal
        opener={internalNoteModalOpener}
        title={intl.formatMessage({ id: "models.internalNote.editInternalNote" })}
      >
        {() => (
          <InternalNoteForm
            consult={consult}
            internalNote={internalNoteBeingEdited}
            journeyId={journey.id}
            onCancel={internalNoteModalOpener.close}
            onSuccess={internalNoteModalOpener.close}
          />
        )}
      </Modal>

      <Modal
        opener={discussionItemModalOpener}
        ref={discussionItemModalRef}
        title={intl.formatMessage({
          id: discussionItemBeingEdited
            ? "models.consultReportDiscussionItem.editDiscussionItem"
            : "models.consultReportDiscussionItem.addDiscussionItem"
        })}
      >
        {({ modalDisplayed }) => (
          <>
            {consult.consultReport && (
              <ConsultReportDiscussionItemForm
                autofocus={modalDisplayed}
                consultReportDiscussionItem={discussionItemBeingEdited}
                consultReportId={consult.consultReport.id}
                journeyId={journey.id}
                onCancel={discussionItemModalOpener.close}
                onError={handleDiscussionItemError}
                onSuccess={discussionItemModalOpener.close}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ConsultReport;
